import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword
} from "aws-amplify/auth";
import config from "lib/fr-config";

// 모든 상수값은 여기에 저장하자.

/**
 * service-amp.js
 *
 * 이 파일은 Amplify Auth v6를 사용해서 기존 service.js를 대체하기 위한 인증 서비스 파일입니다.
 *
 * 특징:
 * - 기존 service.js 와 비슷한 반환 형식 { code, res } 유지
 * - 이메일/비밀번호 사용자 흐름 처리 가능
 * - Google 로그인 redirect 흐름도 같은 서비스 파일 안에서 처리 가능
 *
 * 알아둘 점:
 * 1) 토큰 저장은 Amplify가 내부적으로 관리합니다.
 * 2) fetchAuthSession() 은 현재 세션의 id/access token 을 가져오고 자동 갱신도 처리합니다.
 * 3) Amplify는 refreshToken을 직접 꺼내 쓰는 방식을 권장하지 않으므로,
 *    기존 인터페이스 호환을 위해 refreshToken 자리는 제거했습니다.
 * 4) Google 로그인은 signInWithRedirect({ provider: "Google" }) 로 시작합니다.
 */

/**
 * 에러 객체를 기존 service.js 와 유사한 문자열 형태로 정리하는 함수
 * - fnType 으로 어떤 함수에서 난 에러인지 표시
 */
const getErrorMessage = (errorInput, fnType) => {
  const text =
    errorInput && errorInput.message && errorInput.name
      ? `name:"${errorInput.name}", message:"${
          errorInput.message
        }", raw:"${JSON.stringify(errorInput)}"`
      : (errorInput &&
          (errorInput.message || errorInput.name || errorInput.code)) ||
        JSON.stringify(errorInput);

  return `[AMPLIFY_AUTH_ERR - type:"${fnType}"] ${text}`;
};

const Service = (function () {
  /**
   * Amplify.configure() 가 한 번이라도 실행되었는지 표시하는 플래그
   * - 중복 configure 호출을 줄이기 위해 사용
   */
  let isConfigured = false;

  /**
   * 단일 값 또는 배열 값을 항상 배열 형태로 맞춰주는 함수
   * - redirectSignIn / redirectSignOut 설정 시 사용
   */
  const toArray = value => {
    if (!value) return [];
    return Array.isArray(value)
      ? value.filter(Boolean)
      : [value].filter(Boolean);
  };

  /**
   * OAuth/Hosted UI 설정이 완성되었는지 확인하기 위한 함수
   * - Google 로그인 가능 여부 점검에 사용
   */
  const getResolvedOAuthConfig = override => {
    const oauthDomain =
      override?.oauthDomain ||
      config.cognito.OAUTH_DOMAIN ||
      config.cognito.DOMAIN ||
      null;

    const redirectSignIn =
      override?.redirectSignIn ||
      config.cognito.OAUTH_REDIRECT_SIGN_IN ||
      config.cognito.REDIRECT_SIGN_IN ||
      null;

    const redirectSignOut =
      override?.redirectSignOut ||
      config.cognito.OAUTH_REDIRECT_SIGN_OUT ||
      config.cognito.REDIRECT_SIGN_OUT ||
      null;

    const redirectSignInList = toArray(redirectSignIn);
    const redirectSignOutList = toArray(redirectSignOut);

    return {
      domain: oauthDomain,
      redirectSignIn: redirectSignInList,
      redirectSignOut: redirectSignOutList,
      isReady:
        Boolean(oauthDomain) &&
        redirectSignInList.length > 0 &&
        redirectSignOutList.length > 0
    };
  };

  /**
   * Amplify 설정 객체를 생성하는 함수
   * - fr-config 의 Cognito 설정을 읽어 기본값 구성
   * - OAuth 설정이 있으면 Google 같은 소셜 로그인도 가능
   */
  const buildAmplifyConfig = (override = {}) => {
    const cognitoConfig = {
      userPoolId: config.cognito.USER_POOL_ID,
      userPoolClientId: config.cognito.APP_CLIENT_ID,
      loginWith: {
        email: true
      }
    };

    const oauthConfig = getResolvedOAuthConfig(override);

    if (oauthConfig.isReady) {
      cognitoConfig.loginWith.oauth = {
        domain: oauthConfig.domain,
        scopes: ["openid", "email", "profile"],
        redirectSignIn: oauthConfig.redirectSignIn,
        redirectSignOut: oauthConfig.redirectSignOut,
        responseType: "code"
      };
    }

    return {
      Auth: {
        Cognito: {
          ...cognitoConfig,
          ...(override.Auth && override.Auth.Cognito
            ? override.Auth.Cognito
            : {})
        }
      }
    };
  };

  /**
   * Amplify가 아직 설정되지 않았다면 configure를 실행하는 함수
   * - 앱 시작 시 한 번만 자동 설정되도록 처리
   */
  const ensureConfigured = override => {
    if (isConfigured && !override) {
      return;
    }

    Amplify.configure(buildAmplifyConfig(override || {}));
    isConfigured = true;
  };

  /**
   * Amplify가 반환한 token 객체를 문자열 JWT로 바꿔주는 함수
   * - token이 문자열이면 그대로 반환
   * - token 객체면 toString() 호출
   */
  const jwtToString = token => {
    if (!token) {
      return null;
    }

    if (typeof token === "string") {
      return token;
    }

    if (typeof token.toString === "function") {
      return token.toString();
    }

    return String(token);
  };

  /**
   * JWT 문자열에서 payload를 디코딩해 객체로 반환하는 함수
   * - exp 같은 만료 정보 확인에 사용
   */
  const decodeJwtPayload = token => {
    if (!token || typeof token !== "string") {
      return null;
    }

    try {
      const [, payloadPart] = token.split(".");
      if (!payloadPart) {
        return null;
      }

      const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        "="
      );

      if (typeof atob !== "function") {
        return null;
      }

      const decoded = atob(padded);

      const utf8 = decodeURIComponent(
        decoded
          .split("")
          .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join("")
      );

      return JSON.parse(utf8);
    } catch (error) {
      return null;
    }
  };

  /**
   * JWT 문자열을 기존 service.js 와 비슷한 token 객체 형태로 변환하는 함수
   * - getJwtToken()
   * - getExpiration()
   * - decodePayload()
   * 메서드를 제공
   */
  const createJwtTokenObject = jwtToken => {
    const payload = decodeJwtPayload(jwtToken);

    return {
      jwtToken,
      payload,
      getJwtToken() {
        return jwtToken;
      },
      getExpiration() {
        return payload && payload.exp ? payload.exp : null;
      },
      decodePayload() {
        return payload;
      }
    };
  };

  /**
   * Amplify는 refreshToken을 직접 노출하는 사용을 권장하지 않기 때문에,
   * 기존 인터페이스 호환을 위한 placeholder 객체를 반환하는 함수
   */
  // const createRefreshTokenPlaceholder = () => ({
  //   token: null,
  //   getToken() {
  //     return null;
  //   }
  // });

  /**
   * Amplify 세션 객체를 기존 service.js 와 유사한 tokenObj 구조로 바꾸는 함수
   * - idToken
   * - accessToken
   * - refreshToken(placeholder)
   */
  const sessionToLegacyTokenObj = session => {
    const idToken = jwtToString(
      session && session.tokens && session.tokens.idToken
    );
    const accessToken = jwtToString(
      session && session.tokens && session.tokens.accessToken
    );

    if (!idToken || !accessToken) {
      return null;
    }

    return {
      idToken: createJwtTokenObject(idToken),
      accessToken: createJwtTokenObject(accessToken)
      /**
       * 과거 코드(service.js)에서는 refreshToken 직접 사용했었는데,
       * 정책상 비공개 하는듯 해서 주석처리함(삭제해도됨)
       * */
      // refreshToken: createRefreshTokenPlaceholder()
    };
  };

  /**
   * Cognito/Amplify에서 자주 발생하는 알려진 에러 코드를 분류하는 함수
   * - 기존 service.js 처럼 known error 는 code 그대로 반환하도록 사용
   */
  const mapKnownAuthErrorCode = error => {
    const code = error && (error.name || error.code);

    switch (code) {
      case "NotAuthorizedException":
      case "UserNotFoundException":
      case "UsernameExistsException":
      case "UserLambdaValidationException":
      case "InvalidParameterException":
      case "CodeMismatchException":
      case "LimitExceededException":
      case "UserNotConfirmedException":
      case "ExpiredCodeException":
      case "ConfirmSignUpException":
      case "ConfirmForgotPasswordException":
      case "ForgotPasswordException":
      case "ChangePasswordException":
      case "AuthTokenConfigException":
      case "AuthValidationErrorCode":
        return code;
      default:
        return null;
    }
  };

  /**
   * 지연 대기 함수
   * - redirect 로그인 직후 토큰 반영을 잠시 기다릴 때 사용
   */
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * 현재 로그인된 사용자의 provider를 최대한 추론하는 함수
   * - signInWithRedirect 사용 시 signInDetails가 없을 수 있으므로
   *   username / cognito:username 등을 같이 참고
   */
  const inferSignInProvider = ({ user, tokenObj }) => {
    const username =
      (user && user.username) ||
      (tokenObj &&
        tokenObj.idToken &&
        tokenObj.idToken.decodePayload &&
        tokenObj.idToken.decodePayload() &&
        tokenObj.idToken.decodePayload()["cognito:username"]) ||
      null;

    if (typeof username === "string") {
      if (username.indexOf("Google_") === 0) {
        return "Google";
      }

      if (
        username.indexOf("LoginWithAmazon_") === 0 ||
        username.indexOf("Facebook_") === 0 ||
        username.indexOf("SignInWithApple_") === 0
      ) {
        return "SOCIAL";
      }
    }

    return "Cognito";
  };

  /**
   * 현재 사용자 정보와 세션을 합쳐 정규화된 사용자 정보 객체를 만드는 함수
   * - provider, tokenObj, payload 등을 한 번에 반환
   */
  const normalizeCurrentUserInfo = ({ user, session }) => {
    const tokenObj = sessionToLegacyTokenObj(session);
    const provider = inferSignInProvider({ user, tokenObj });
    const idTokenPayload =
      tokenObj && tokenObj.idToken && tokenObj.idToken.decodePayload
        ? tokenObj.idToken.decodePayload()
        : null;
    const accessTokenPayload =
      tokenObj && tokenObj.accessToken && tokenObj.accessToken.decodePayload
        ? tokenObj.accessToken.decodePayload()
        : null;

    return {
      username: user && user.username ? user.username : null,
      userId: user && user.userId ? user.userId : null,
      signInDetails: user && user.signInDetails ? user.signInDetails : null,
      signInProvider: provider,
      isGoogleUser: provider === "Google",
      isSocialLogin: provider !== "Cognito",
      tokenObj,
      idTokenPayload,
      accessTokenPayload
    };
  };

  const _configure = function (override) {
    /**
     * Amplify를 수동으로 설정하고 싶을 때 사용하는 함수
     * - 필요 시 override 설정을 넘겨서 재구성 가능
     */

    ensureConfigured(override || {});
  };

  const _isGoogleLoginReady = function (override) {
    /**
     * 현재 OAuth/Google 로그인 설정이 준비되었는지 확인하는 함수
     * - Google 버튼을 노출할지 판단할 때 사용
     */

    const oauthConfig = getResolvedOAuthConfig(override || {});
    return { code: "ok", res: oauthConfig };
  };

  const _getUserToken = async function () {
    /**
     * 현재 로그인된 사용자의 토큰을 가져오는 함수
     * - 로그인 상태가 아니면 null 반환
     * - 로그인 상태면 idToken / accessToken 반환
     *
     * 내메모:
     * logInWithGoogle() 실행후 구글 로그인 되어 redirect 등록한 url로
     * 돌아올때 url 뒤에 ?code=xxxx&state=yyy같은 값을 가지는데,
     * 이 상태에서 getUserToken() 실행되면 위의 code,state값은 사라지고,
     * 대신 해당 코그니토 사용자의 토큰이 알아서 저장되더라.
     */

    ensureConfigured();

    try {
      const session = await fetchAuthSession();

      if (!session || !session.tokens || !session.tokens.accessToken) {
        return { code: "ok", res: null };
      }

      return { code: "ok", res: sessionToLegacyTokenObj(session) };
    } catch (error) {
      return { code: "ok", res: null };
    }
  };

  const _logIn = async function (email, password) {
    /**
     * 이메일/비밀번호 로그인 함수
     * - signIn 실행 후 세션을 다시 읽어 tokenObj 반환
     * - 추가 인증 단계(MFA 등)가 남아 있으면 nextStep 정보를 그대로 반환
     */

    ensureConfigured();

    try {
      const result = await signIn({
        username: email,
        password
      });

      if (result && result.nextStep && result.nextStep.signInStep !== "DONE") {
        return {
          code: result.nextStep.signInStep || "error",
          res: result
        };
      }

      const session = await fetchAuthSession();
      return { code: "ok", res: sessionToLegacyTokenObj(session) };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return { code: "error", res: getErrorMessage(error, "logIn") };
    }
  };

  const _confirmLogIn = async function (challengeResponse) {
    /**
     * 로그인 후 추가 챌린지 응답 함수
     * - MFA, custom challenge 같은 흐름이 생길 경우 사용
     * - 기존 service.js에는 없지만 Amplify에서는 필요할 수 있어서 추가
     */

    ensureConfigured();

    try {
      const result = await confirmSignIn({ challengeResponse });

      if (result && result.nextStep && result.nextStep.signInStep !== "DONE") {
        return {
          code: result.nextStep.signInStep || "error",
          res: result
        };
      }

      const session = await fetchAuthSession();
      return { code: "ok", res: sessionToLegacyTokenObj(session) };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return { code: "error", res: getErrorMessage(error, "confirmLogIn") };
    }
  };

  const _signUp = async function (email, password) {
    /**
     * 이메일/비밀번호 회원가입 함수
     * - email을 username으로 사용
     * - userAttributes.email도 함께 전달
     */

    ensureConfigured();

    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      });

      return { code: "ok", res: result };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      } else {
        return { code: "error", res: getErrorMessage(error, "signUp") };
      }
    }
  };

  const _confirmSignUp = async function (email, confirmationCode) {
    /**
     * 회원가입 인증 코드 확인 함수
     * - 이메일로 받은 confirmationCode를 사용해 가입 완료
     */

    ensureConfigured();

    try {
      const result = await confirmSignUp({
        username: email,
        confirmationCode
      });

      return { code: "ok", res: result };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return { code: "error", res: getErrorMessage(error, "confirmSignUp") };
    }
  };

  const _resendSignUpCode = async function (email) {
    /**
     * 회원가입 인증 코드 재발송 함수
     * - 가입 후 인증 메일을 다시 받고 싶을 때 사용
     */

    ensureConfigured();

    try {
      const result = await resendSignUpCode({
        username: email
      });

      return { code: "ok", res: result };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return {
        code: "error",
        res: getErrorMessage(error, "resendSignUpCode")
      };
    }
  };

  const _logOut = async function () {
    /**
     * 현재 디바이스 기준 로그아웃 함수
     * - Amplify 내부 세션 제거
     * - Google 자체 세션까지 종료되지는 않음
     *
     * 내메모:
     * 이메일 로그인의 경우 아니지만,
     * 구글 로그인의 경우 signOut() 실행시 리다이렉트됨.
     */

    ensureConfigured();
    await signOut();
  };

  const _logOutForAllDevice = async function () {
    /**
     * 모든 디바이스에서 로그아웃하는 함수
     * - Cognito global sign-out 실행
     *
     * 내메모:
     * 구글로그인 회원이 이 기능 활성화하려면
     * scope에 "aws.cognito.signin.user.admin" 값이 추가되어야 한다.
     * 근데 "aws.cognito.signin.user.admin"를 scope에 추가시 보안위험 증가.
     * (deleteUser까지 가능해진대. 검색해보자.)
     * 그래서 이기능 구글로그인에 적용하려면, 모든기기로그아웃 함수를 백엔드에서 실행하고,
     * 프론트에서 백엔드로 요청하는 방식이 젤 보안에 좋다.
     * ("aws.cognito.signin.user.admin" 없어도 됌. 어짜피 백엔드에서 할거니깐)
     * 기능 원하면 자세한건 검색으로 해결하자.
     */

    ensureConfigured();

    try {
      await signOut({ global: true });
      return { code: "ok", res: "SUCCESS" };
    } catch (error) {
      return {
        code: "error",
        res: getErrorMessage(error, "logOutForAllDevice")
      };
    }
  };

  const _renewToken = async function () {
    /**
     * 세션 강제 갱신 함수
     * - fetchAuthSession({ forceRefresh: true }) 사용
     * - 갱신 성공 시 최신 tokenObj 반환
     */

    ensureConfigured();

    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const tokenObj = sessionToLegacyTokenObj(session);

      if (!tokenObj) {
        return { code: "ok", res: null };
      }

      return { code: "ok", res: tokenObj };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return { code: "error", res: getErrorMessage(error, "renewToken") };
    }
  };

  const _forgotPassword = async function (email) {
    /**
     * 비밀번호 재설정 시작 함수
     * - resetPassword 실행
     * - 보통 인증 코드 발송 단계
     */

    ensureConfigured();

    try {
      const result = await resetPassword({
        username: email
      });

      return { code: "ok", res: result };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return { code: "error", res: getErrorMessage(error, "forgotPassword") };
    }
  };

  const _confirmForgotPassword = async function (email, code, newPassword) {
    /**
     * 비밀번호 재설정 완료 함수
     * - 이메일로 받은 인증 코드와 새 비밀번호를 전달
     */

    ensureConfigured();

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });

      return { code: "ok", res: "SUCCESS" };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return {
        code: "error",
        res: getErrorMessage(error, "confirmForgotPassword")
      };
    }
  };

  const _changePassword = async function (oldPw, newPw) {
    /**
     * 로그인된 사용자의 비밀번호 변경 함수
     * - 기존 비밀번호와 새 비밀번호를 전달
     */

    ensureConfigured();

    try {
      await updatePassword({
        oldPassword: oldPw,
        newPassword: newPw
      });

      return { code: "ok", res: "SUCCESS" };
    } catch (error) {
      const known = mapKnownAuthErrorCode(error);

      if (known) {
        return { code: known, res: null };
      }

      return { code: "error", res: getErrorMessage(error, "changePassword") };
    }
  };

  const _logInWithGoogle = async function () {
    /**
     * Google 로그인 시작 함수
     * - OAuth 설정이 완료되어 있어야 동작
     * - Cognito Hosted UI / redirect 기반 로그인 시작
     *
     * 내메모:
     * 이 함수가 실행되면 구글 로그인 사이트로 이동하여 이메일 선택 ui 진입.
     * 이후 설정해놓은 리다이렉트 주소로 돌아옴 (url 뒤에 ?code=xxxx&state=yyy 함께)
     * 결국 <COGNITO_DOMAIN>으로 링크 걸어두는것과 같은 효과.
     */

    ensureConfigured();

    const oauthConfig = getResolvedOAuthConfig({});
    if (!oauthConfig.isReady) {
      return {
        code: "OAuthNotConfigured",
        res: "Google 로그인용 OAuth 설정(domain / redirectSignIn / redirectSignOut)이 없습니다."
      };
    }

    try {
      await signInWithRedirect({ provider: "Google" });
      return { code: "ok", res: null };
    } catch (error) {
      return {
        code: "error",
        res: getErrorMessage(error, "logInWithGoogle")
      };
    }
  };

  const _completeGoogleLogin = async function (options = {}) {
    /**
     * redirect 방식의 Google 로그인 완료 후 현재 로그인 결과를 읽는 함수
     * - callback 페이지 또는 앱 첫 진입 시 사용
     * - 세션 확보 여부를 확인하고 사용자 정보까지 반환
     */

    ensureConfigured();

    const maxAttempts =
      typeof options.maxAttempts === "number" ? options.maxAttempts : 10;
    const intervalMs =
      typeof options.intervalMs === "number" ? options.intervalMs : 300;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const [user, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession()
        ]);

        if (session && session.tokens && session.tokens.accessToken) {
          return {
            code: "ok",
            res: normalizeCurrentUserInfo({ user, session })
          };
        }
      } catch (error) {
        // 아직 redirect 처리 중이면 잠시 후 재시도
      }

      if (attempt < maxAttempts - 1) {
        await sleep(intervalMs);
      }
    }

    return { code: "ok", res: null };
  };

  const _getCurrentUserInfo = async function () {
    /**
     * 현재 로그인된 사용자 정보를 가져오는 함수
     * - 로그인 상태 확인
     * - provider 확인
     * - tokenObj / payload 확인 등에 사용
     *
     * 내메모:
     * 구글 로그인인지, 이메일/비밀번호 로그인인지 이 함수로 판단못함
     * 둘다 "Cognito"라고 출력해버림. 무용지물
     */

    ensureConfigured();

    try {
      const [user, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession()
      ]);

      return {
        code: "ok",
        res: normalizeCurrentUserInfo({ user, session })
      };
    } catch (error) {
      return { code: "ok", res: null };
    }
  };

  const _getCurrentLoginProvider = async function () {
    /**
     * 현재 로그인 사용자의 provider만 간단히 알고 싶을 때 사용하는 함수
     * - 반환값 예: Google / Cognito / SOCIAL
     *
     * 내메모:
     * 구글 로그인인지, 이메일/비밀번호 로그인인지 이 함수로 판단못함
     * 둘다 "Cognito"라고 출력해버림. 무용지물
     */

    ensureConfigured();

    try {
      const [user, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession()
      ]);
      const info = normalizeCurrentUserInfo({ user, session });
      return { code: "ok", res: info ? info.signInProvider : null };
    } catch (error) {
      return { code: "ok", res: null };
    }
  };

  return {
    configure: _configure,
    isGoogleLoginReady: _isGoogleLoginReady,
    getUserToken: _getUserToken,
    logIn: _logIn,
    confirmLogIn: _confirmLogIn,
    signUp: _signUp,
    confirmSignUp: _confirmSignUp,
    resendSignUpCode: _resendSignUpCode,
    logOut: _logOut,
    logOutForAllDevice: _logOutForAllDevice,
    renewToken: _renewToken,
    forgotPassword: _forgotPassword,
    confirmForgotPassword: _confirmForgotPassword,
    changePassword: _changePassword,
    logInWithGoogle: _logInWithGoogle,
    completeGoogleLogin: _completeGoogleLogin,
    getCurrentUserInfo: _getCurrentUserInfo,
    getCurrentLoginProvider: _getCurrentLoginProvider
  };
})();

export default Service;
